import type { FormFieldConfig } from '@/components/ContactForm.tsx';
import { ContactForm } from '@/components/ContactForm.tsx';
import { GradientText, SplitText } from '@/components/GradientText.tsx';
import { Img } from '@/components/Img.tsx';
import { Layout } from '@/components/Layout.tsx';
import { ProcessCarousel } from '@/components/ProcessCarousel.tsx';
import { StatBar } from '@/components/StatBar.tsx';
import {
  ButtonLink,
  Divider,
  Eyebrow,
  FeatureCard,
  Icons,
  Quote,
  SectionIntro,
} from '@/components/ui.tsx';
import { AUDIENCE_OPTIONS, FAQS, REFERRAL_OPTIONS } from '@/data/content.ts';
import { PRESS_LOGOS, QUOTES, SITE, WEBHOOKS } from '@/data/site.ts';
import { asset, href } from '@/lib/paths.ts';

const CONTACT_FIELDS: readonly FormFieldConfig[] = [
  { name: 'name', label: 'Full Name', type: 'text', required: true },
  { name: 'email', label: 'Email Address', type: 'email', required: true },
  { name: 'phone', label: 'Phone Number', type: 'tel' },
  {
    name: 'interest',
    label: 'I Am A',
    type: 'select',
    options: AUDIENCE_OPTIONS,
  },
  {
    name: 'referral',
    label: 'How Did You Hear About Us?',
    type: 'select',
    options: REFERRAL_OPTIONS,
  },
  {
    name: 'message',
    label: 'Message',
    type: 'textarea',
    placeholder: 'Tell us a bit about your situation...',
  },
];

export function Home() {
  return (
    <Layout currentPath="/">
      {/* ===== Hero ===== */}
      <section className="hero">
        <div className="hero__bg">
          {/* LCP element on the home page: eager, high priority, responsive. */}
          <Img
            src="images/hero-home.webp"
            alt=""
            width={1920}
            height={1280}
            priority
          />
        </div>
        <div className="hero__overlay" />
        <div className="container">
          <div className="hero__content">
            <Eyebrow>Wealth Preservation Strategies</Eyebrow>
            <h1>
              Turn Tax Liability Into{' '}
              <GradientText immediate startDelay={0.5}>
                Tax Equity Investment.
              </GradientText>
            </h1>
            <p className="lead fade-in fade-in-delay-2">
              We help high net worth individuals offset tax equity by investing
              into energy programs, delivering up to 40%+ in cash savings.
            </p>
            <div className="fade-in fade-in-delay-3">
              <ButtonLink to="/#contact">Schedule a Consultation</ButtonLink>
            </div>
          </div>
        </div>
      </section>

      <StatBar />

      {/* ===== Mission ===== */}
      <section className="section-pad">
        <div className="container">
          <div className="split">
            <div className="split__image fade-in">
              <Img
                src="images/mission-opportunity.webp"
                alt="Aerial view of a solar panel farm representing renewable energy investment opportunity"
                width={1421}
                height={1066}
                sizes="(max-width: 900px) 100vw, 50vw"
              />
            </div>
            <div className="split__content fade-in fade-in-delay-1">
              <Eyebrow>Our Mission</Eyebrow>
              <h2>
                Turning Tax Liability Into{' '}
                <GradientText as="em" startDelay={0.5}>
                  Opportunity
                </GradientText>
              </h2>
              <p className="lead">
                Lux Financial helps high net worth individuals and their
                companies obtain up to 40%+ in cash savings from the Investment
                Tax Credit (ITC), while building passive income through energy
                programs.{' '}
                <a href={href('/article-tax-equity')} className="inline-link">
                  See how tax equity investments work
                </a>
                .
              </p>
              <Divider />
              <p className="lead">
                As the world becomes increasingly AI-driven and
                energy-dependent, the demand for power will surge. We position
                our clients at the forefront of this opportunity.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ===== Investor tech ===== */}
      <section className="section-pad">
        <div className="container">
          <div className="split split--reverse">
            <div className="split__image split__image--device fade-in">
              <Img
                src="images/investor-portal-laptop.webp"
                alt="Lux Financial investor portal dashboard showing real-time ROI on a laptop"
                width={1200}
                height={745}
                sizes="(max-width: 900px) 100vw, 50vw"
              />
            </div>
            <div className="split__content fade-in fade-in-delay-1">
              <Eyebrow>Best Tech For Investors</Eyebrow>
              <h2>
                Real-Time Visibility Into{' '}
                <GradientText as="em" startDelay={0.5}>
                  Your Returns
                </GradientText>
              </h2>
              <p className="lead">
                Our proprietary investor portal gives you complete transparency
                into your investment performance, ROI, and cash savings, updated
                in real time.
              </p>
              <Divider />
              <p className="lead">
                &ldquo;Lux&rsquo;s tech platform far exceeds those of their
                competitors.&rdquo;
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ===== Solutions overview ===== */}
      <section className="section--forest section-pad">
        <div className="container">
          <SectionIntro>
            <Eyebrow>What We Offer</Eyebrow>
            <h2>Comprehensive Tax Equity Solutions</h2>
          </SectionIntro>
          {/* Responsive feature grid: 1 col on mobile, 2 cols on tablet, 3 on desktop */}
          <div className="grid grid-cols-1 gap-[clamp(1.5rem,3vw,3rem)] md:grid-cols-2 lg:grid-cols-3">
            <FeatureCard icon={Icons.layers()} title="Tax Equity Investments">
              Strategic investment programs designed to offset significant tax
              liability while generating long-term passive income for high net
              worth investors.
            </FeatureCard>
            <FeatureCard
              icon={Icons.activity()}
              title="Real-Time ROI Tracking"
              delayClass="fade-in-delay-1"
            >
              Our proprietary technology platform allows you to monitor your
              investments and returns in real time, far exceeding industry
              competitors.
            </FeatureCard>
            <FeatureCard
              icon={Icons.users()}
              title="Advisor Partnerships"
              delayClass="fade-in-delay-2"
            >
              We work hand-in-hand with CPAs, wealth advisors, and family
              offices to integrate our solutions seamlessly into your
              clients&rsquo; financial strategies.
            </FeatureCard>
          </div>
          <div className="text-center mt-3 fade-in">
            <ButtonLink to="/solutions">Explore Our Solutions</ButtonLink>
          </div>
        </div>
      </section>

      {/* ===== Process ===== */}
      <section className="process section-pad">
        <div className="container">
          <SectionIntro>
            <Eyebrow>The Lux Process</Eyebrow>
            <h2>Four Steps to Cash Savings</h2>
          </SectionIntro>
          <ProcessCarousel />
        </div>
      </section>

      <Quote {...QUOTES.cfoEnergy} />

      {/* ===== Featured in ===== */}
      <div className="media-logos-bar">
        <div className="container--wide">
          <div className="media-logos fade-in">
            <span>As Featured In</span>
            {PRESS_LOGOS.map((logo) =>
              'href' in logo && logo.href ? (
                <a
                  key={logo.alt}
                  href={href(logo.href)}
                  aria-label={logo.linkLabel}
                >
                  <img
                    src={asset(logo.src)}
                    alt={logo.alt}
                    width={logo.width}
                    height={logo.height}
                    loading="lazy"
                  />
                </a>
              ) : (
                <img
                  key={logo.alt}
                  src={asset(logo.src)}
                  alt={logo.alt}
                  width={logo.width}
                  height={logo.height}
                  loading="lazy"
                />
              ),
            )}
          </div>
        </div>
      </div>

      {/* ===== FAQ ===== */}
      <section className="section-pad">
        <div className="container container--narrow">
          <SectionIntro>
            <Eyebrow>Common Questions</Eyebrow>
            <h2>
              <GradientText as="em" startDelay={0.2}>
                Frequently Asked Questions
              </GradientText>
            </h2>
          </SectionIntro>
          <div className="faq-list fade-in fade-in-delay-1">
            {FAQS.map((faq) => (
              <details className="faq-item" key={faq.q}>
                <summary>{faq.q}</summary>
                <p>{faq.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* ===== Contact ===== */}
      <section className="cta-section contact-section section-pad" id="contact">
        <div className="container">
          <div className="contact-grid">
            <div className="fade-in">
              <Eyebrow>Get Started</Eyebrow>
              <h2>
                <SplitText>Ready to Offset Your Tax Liability?</SplitText>
              </h2>
              <p className="lead">
                A team member will reach out within 24&ndash;48 hours to
                schedule your personalized consultation.
              </p>
              <Divider />
              <div className="contact-info__item">
                <h4>Phone</h4>
                <p>
                  <a href={SITE.phoneHref}>{SITE.phone}</a>
                </p>
              </div>
              <div className="contact-info__item">
                <h4>Headquarters</h4>
                <p>
                  <a
                    href={SITE.mapsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {SITE.address.street}
                    <br />
                    {SITE.address.locality}, {SITE.address.region}{' '}
                    {SITE.address.postalCode}
                  </a>
                </p>
              </div>
            </div>
            <div className="contact-form fade-in fade-in-delay-1">
              <ContactForm
                action={WEBHOOKS.contact}
                turnstileAction="contact"
                fields={CONTACT_FIELDS}
                submitLabel="Request Consultation"
                successTitle="Thank you — your request is in."
                successBody="A member of our team will reach out within 24–48 hours to schedule your personalized consultation."
              />
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
